import { useState, useEffect, useRef } from 'react';
import useLockBodyScroll from '@hooks/useLockBodyScroll';
import ReactDOM from 'react-dom';

const ModalComponent = ({ show, closeModal, children, className, closeModalConfirmationMessage, id }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const timeoutRef = useRef(null);
  useLockBodyScroll(show);

  const focusOnVisibleModal = () => {
    const visibleModal = document.querySelector('.overlay-background-react[style="display: block;"]');
    if (visibleModal) {
      visibleModal.focus();
    }
  };

  const handleModalClosing = () => {
    setIsClosing(true);
    timeoutRef.current = setTimeout(() => {
      closeModal();
      setIsVisible(false);
      setIsClosing(false);
      focusOnVisibleModal();
    }, 120);
  };

  const handleModalVisibility = (_show) => {
    if (_show) {
      setIsVisible(true);
      focusOnVisibleModal();
    } else {
      if (!isVisible || isClosing) {
        return;
      }
      handleModalClosing();
    }
  };

  const confirmCloseModal = () => {
    if (!closeModalConfirmationMessage) return true;
    return window.confirm(closeModalConfirmationMessage);
  };

  const handleModalKeyUp = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      if (confirmCloseModal()) {
        handleModalVisibility(false);
      }
    }
  };

  const handleModalClick = (e) => {
    if (typeof e.target.className !== 'string') return;
    if (e.target.className.includes('overlay-cancel')) {
      e.stopPropagation();
      if (confirmCloseModal()) {
        handleModalVisibility(false);
      }
    }
  };

  useEffect(() => {
    handleModalVisibility(show);
  }, [show]);

  const getModalRoot = () => {
    try {
      let modalRoot = document.getElementById('modal-root');
      
      if (!modalRoot) {
        modalRoot = document.createElement('div');
        modalRoot.id = 'modal-root';
        document.body.appendChild(modalRoot);
      }
      return modalRoot;
    } catch (error) {
      console.error('ModalComponent: Error getting modal root:', error);
      return document.body;
    }
  };

  if (!show && !isVisible) {
    return null;
  }

  try {
    return ReactDOM.createPortal(
      <div
        id={id}
        className={`overlay-background overlay-background-react ${className ? className : ''} ${isClosing ? 'overlay-closing' : ''}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: show || isVisible ? 'block' : 'none' }}
        onClick={handleModalClick}
        onKeyUp={handleModalKeyUp}
      >
        {children}
      </div>,
      getModalRoot()
    );
  } catch (error) {
    console.error('ModalComponent: Error creating portal:', error);
    return (
      <div
        id={id}
        className={`overlay-background overlay-background-react ${className ? className : ''} ${isClosing ? 'overlay-closing' : ''}`}
        tabIndex="-1"
        role="dialog"
        style={{ display: show || isVisible ? 'block' : 'none' }}
        onClick={handleModalClick}
        onKeyUp={handleModalKeyUp}
      >
        {children}
      </div>
    );
  }
};

export default ModalComponent;
